import { Navigation } from 'components';
import * as userRoles from 'constants/roles';
import {
  BRAND_MANAGER,
  CATEGORIES,
  COLORS,
  INVENTORIES,
  PRIVACY_POLICY,
  SIZES,
  TERMS_OF_SERVICE,
} from 'constants/routes';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toastr } from 'utils';
import { isMobilePhone } from 'utils/mobileDevice';
import { useFetchLocations } from 'api/locations/hooks';
import { useAuthStore } from 'stores/authStore';
import { useMobileAppPromptModalStore } from 'stores/mobileAppPromptModalStore';
import { useWhatsNewModalStore } from 'stores/whatsNewModalStore';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { useRegionSwitcherStore } from 'stores/regionSwitcherStore';
import { useLogout } from 'api/auth/hooks';
import { WHATS_NEW_RELEASE } from 'components/WhatsNewModal/WhatsNewModal';
import ProfileSwitcher from '../UserProfile/ProfileSwitcher';
import RegionSwitcher from '../UserProfile/RegionSwitcher';
import { menuItems, superUserMenuItem } from './menu';

const SHOPIFY_MANAGED_PRODUCT_LINKS = [COLORS, INVENTORIES, SIZES, CATEGORIES];

const NavLinksWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #6c757d;
    border-radius: 4px;
  }
  scrollbar-width: thin;
  scrollbar-color: #6c757d transparent;
`;

const MobileAppNavItem = styled.li.attrs({
  className: 'nav-item',
})`
  display: none;

  @media (max-width: 767px) {
    display: block;
  }
`;

const MobileAppNavButton = styled.button.attrs({
  className: 'nav-link',
  type: 'button',
})`
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
`;

const MobileAppNavLabel = styled.span`
  display: inline-block;
  font-size: 13px;
`;

const BottomSection = styled.div`
  position: relative;
  flex-shrink: 0;
  padding-bottom: 24px;
`;

const RegulationsContainer = styled.div`
  width: 100%;
  position: absolute;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  gap: 4px;
  a {
    color: #65757d;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  span {
    color: #65757d;
  }
`;

const SideNavigation = ({ compact, toggleNavigation }) => {
  const { t } = useTranslation();
  const { mutate: logoutUser } = useLogout();
  const role = useAuthStore((state) => state.role);
  const isSuperUser = useAuthStore((state) => state.isSuperUser);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const rawRegions = useAuthStore((state) => state.user?.admin_details?.brand?.regions);
  const selectedRegion = useSelectedProfileStore((state) => state.selectedRegionUid);
  const isOwnerPortalUser = [userRoles.ADMIN].includes(role);
  const isRetailer = role === userRoles.RETAILER;
  const translationPrefix = isOwnerPortalUser ? 'ownerPortal:menu.' : 'menu:';
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [profileSwitcherOpened, setProfileSwitcherOpened] = useState(undefined);
  const regionSwitcherOpened = useRegionSwitcherStore((state) => state.isOpen);
  const toggleRegionSwitcher = useRegionSwitcherStore((state) => state.toggleRegionSwitcher);
  const closeRegionSwitcher = useRegionSwitcherStore((state) => state.closeRegionSwitcher);
  const openMobileAppPromptModal = useMobileAppPromptModalStore(
    (state) => state.openMobileAppPromptModal,
  );
  const setWhatsNewModalOpen = useWhatsNewModalStore((state) => state.setIsOpen);
  const bottomSectionRef = useRef(null);

  useEffect(() => {
    setIsMobileDevice(isMobilePhone());
  }, []);

  useEffect(() => {
    if (!profileSwitcherOpened && !regionSwitcherOpened) return undefined;

    const handleClickOutside = (e) => {
      if (!bottomSectionRef.current) return;
      if (!document.body.contains(e.target)) return;
      if (!bottomSectionRef.current.contains(e.target)) {
        setProfileSwitcherOpened(false);
        closeRegionSwitcher();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [profileSwitcherOpened, regionSwitcherOpened, closeRegionSwitcher]);

  const { data: retailerLocations = [], isLoading: isLoadingRetailerLocations } = useFetchLocations(
    undefined,
    {
      enabled: isRetailer,
    },
  );
  const canShowRetailerLocations = !isRetailer || retailerLocations.length > 0;

  const regions = useMemo(() => rawRegions ?? [], [rawRegions]);
  const activeRegion = useMemo(
    () => regions.find((region) => region.uid === selectedRegion) || regions[0],
    [regions, selectedRegion],
  );
  const isShopifyIntegrated = Boolean(activeRegion?.shopify_integration?.uid);

  const navigationMenuItems = useMemo(
    () =>
      menuItems
        .map((item) => {
          if (item.id !== '4' || !isShopifyIntegrated) return item;

          return {
            ...item,
            items: item.items?.filter(
              (subItem) => !SHOPIFY_MANAGED_PRODUCT_LINKS.includes(subItem.link),
            ),
          };
        })
        .filter((item) => {
          if (item.id !== '7') return true;
          if (isLoadingRetailerLocations) return false;

          return canShowRetailerLocations;
        }),
    [canShowRetailerLocations, isLoadingRetailerLocations, isShopifyIntegrated],
  );

  const buildMenu = (items, subItem = false) =>
    items.map((item) => {
      if (item && item?.items && (!item.roles || item?.roles?.includes(role))) {
        return (
          <Navigation.Link
            key={`navlink-${item.id}`}
            id={item.id}
            to={item.link}
            name={[userRoles.ADMIN].includes(role) ? item.adminToolLabel || item.label : item.label}
            icon={item.icon}
            renderIcon={item.renderIcon}
            subItem={subItem}
            guard={item?.guard}
            className={item.className}
            end={item.end}
            activePaths={item.activePaths}
          >
            {buildMenu(item.items, true)}
          </Navigation.Link>
        );
      }
      if (item && (!item.roles || item?.roles?.includes(role))) {
        return (
          <Navigation.Link
            key={`navlink-${item.id}`}
            id={item.id}
            to={item.link}
            name={[userRoles.ADMIN].includes(role) ? item.adminToolLabel || item.label : item.label}
            icon={item.icon}
            renderIcon={item.renderIcon}
            subItem={subItem}
            className={item.className}
            end={item.end}
            activePaths={item.activePaths}
          />
        );
      }
      return null;
    });

  return (
    <Navigation
      toggleNavigation={toggleNavigation}
      compact={compact}
      profileSwitcherOpened={profileSwitcherOpened}
      items={isSuperUser ? [superUserMenuItem, ...navigationMenuItems] : navigationMenuItems}
    >
      <NavLinksWrapper>
        {isMobileDevice && (
          <MobileAppNavItem>
            <MobileAppNavButton onClick={openMobileAppPromptModal}>
              <i className="fe fe-smartphone" />
              <MobileAppNavLabel>{t('menu:mobileApp')}</MobileAppNavLabel>
            </MobileAppNavButton>
          </MobileAppNavItem>
        )}
        {isSuperUser && buildMenu([superUserMenuItem])}
        {buildMenu(navigationMenuItems)}
      </NavLinksWrapper>
      <BottomSection ref={bottomSectionRef}>
        <ProfileSwitcher
          compact={compact}
          profileSwitcherOpened={profileSwitcherOpened}
          setProfileSwitcherOpened={setProfileSwitcherOpened}
        />
        {isOwnerPortalUser && (
          <RegionSwitcher
            compact={compact}
            regionSwitcherOpened={regionSwitcherOpened}
            setRegionSwitcherOpened={toggleRegionSwitcher}
          />
        )}
        {isOwnerPortalUser ? (
          <Navigation.Link
            active={false}
            icon="fe-user"
            name={t(`${translationPrefix}profile`)}
            to={BRAND_MANAGER}
          />
        ) : null}
        {!isImpersonating &&
          WHATS_NEW_RELEASE.items.some((item) => item.audiences.includes(role)) && (
            <Navigation.Link
              name={t(`${translationPrefix}whatsNew`)}
              icon="fe-gift"
              active={false}
              to="#"
              onClick={() => setWhatsNewModalOpen(true)}
            />
          )}
        <Navigation.Link
          name={t(`${translationPrefix}logout`)}
          icon="fe-log-out"
          active={false}
          to="#"
          onClick={() =>
            toastr.confirm(t('toastr:logoutQuestion'), {
              onOk: () => {
                logoutUser();
              },
              description: t('toastr:logoutQuestionDescription'),
            })
          }
        />
        {!compact && (
          <RegulationsContainer>
            <Link to={TERMS_OF_SERVICE}>Terms of Service</Link>
            <span>•</span>
            <Link to={PRIVACY_POLICY}>Privacy Policy</Link>
          </RegulationsContainer>
        )}
      </BottomSection>
    </Navigation>
  );
};

SideNavigation.propTypes = {
  compact: PropTypes.bool,
  toggleNavigation: PropTypes.func,
};

export default SideNavigation;
